import { useEffect, useState } from "react";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const testimonials = [
  { company: "DeepSeek", metric: "10x", description: "Faster Development", icon: "🚀" },
  { company: "GPT-4o", metric: "98%", description: "User Satisfaction", icon: "⭐" },
  { company: "Claude", metric: "5min", description: "Avg Response Time", icon: "⚡" },
  { company: "Gemini", metric: "100+", description: "Enterprise Clients", icon: "🏢" },
];

export function LandingPage() {
  const navigate = useNavigate();
  const [scrollOffset, setScrollOffset] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setScrollOffset((prev) => (prev + 1) % 100);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 text-gray-900 overflow-hidden">
      {/* 导航栏 */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">Persona AI</span>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/auth")}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 transition"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate("/auth")}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* 主标题 */}
          <div className="text-center mb-12">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600">
                Define Your
              </span>
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600">
                AI Personas
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Create custom AI roles with unique capabilities.
              <br />
              <span className="text-gray-400">Every persona, built just for you.</span>
            </p>

            {/* CTA 按钮 */}
            <div className="flex items-center justify-center gap-4 mb-16">
              <button
                onClick={() => navigate("/auth")}
                className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold text-lg hover:opacity-90 transition flex items-center gap-2 shadow-lg shadow-purple-500/25"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </button>
              
              <button
                onClick={() => navigate("/auth")}
                className="px-8 py-4 bg-white border border-gray-300 rounded-xl font-semibold text-lg hover:bg-gray-50 transition flex items-center gap-2"
              >
                <Play className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Watch Demo</span>
              </button>
            </div>
          </div>

          {/* 社会证明滚动区 */}
          <div className="relative overflow-hidden mb-16">
            <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10" />
            <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10" />
            
            <div 
              className="flex gap-6"
              style={{
                transform: `translateX(-${scrollOffset}%)`,
              }}
            >
              {[...testimonials, ...testimonials, ...testimonials].map((item, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-64 bg-white border border-gray-200 rounded-2xl p-6 hover:border-purple-300 hover:shadow-lg transition"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{item.icon}</span>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{item.metric}</div>
                      <div className="text-sm text-gray-500">{item.company}</div>
                    </div>
                  </div>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 核心能力展示 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg hover:border-blue-300 transition group">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-100 transition">
                <span className="text-2xl">🎨</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Unlimited Personas</h3>
              <p className="text-gray-600">Create any number of AI personas, each with unique personality and expertise</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg hover:border-purple-300 transition group">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-100 transition">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Multi-Model Support</h3>
              <p className="text-gray-600">Integrate DeepSeek, GPT-4o, Hunyuan and other leading models, switch freely</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg hover:border-pink-300 transition group">
              <div className="w-12 h-12 bg-pink-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-pink-100 transition">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Canvas Collaboration</h3>
              <p className="text-gray-600">Build AI workflows on infinite canvas, multiple roles collaborate on tasks</p>
            </div>
          </div>

          {/* 代码示例 */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="border-b border-gray-200 px-6 py-3 flex items-center gap-2 bg-gray-50">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <span className="ml-4 text-sm text-gray-500">persona.config.ts</span>
            </div>
            <pre className="p-6 text-sm overflow-x-auto bg-gray-50">
              <code className="text-gray-700">
{`// Create your first AI persona
const myPersona = {
  name: "Product Manager",
  icon: "📝",
  personality: "Professional, Detail-oriented, Results-driven",
  skills: [
    "Requirement Analysis",
    "PRD Writing",
    "User Research",
    "Data-driven Decisions"
  ],
  model: "deepseek-v3"
};

// Connect multiple roles on canvas
const workflow = new Canvas()
  .addNode(myPersona)
  .addNode(developerPersona)
  .connect(myPersona, developerPersona);`}
              </code>
            </pre>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center text-gray-500 text-sm">
          <p>© 2026 Persona AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
